import React, { useState } from 'react';
import { Grid, Switch, TextField, Divider } from '@mui/material';
import { Download, TrendingUp, Percent, BarChart3, DollarSign } from 'lucide-react';
import { PageHeader, Card, StatCard, SectionTitle, PillTabs } from '../../components/common/UIComponents';

const inputSx = {
  '& .MuiOutlinedInput-root':{ background:'var(--bg-muted)', borderRadius:'10px !important', fontSize:13, fontFamily:'DM Sans,sans-serif', '& fieldset':{ borderColor:'var(--border)' }, '&:hover fieldset':{ borderColor:'#d4c4b0' }, '&.Mui-focused fieldset':{ borderColor:'var(--brand)' } },
  '& .MuiInputLabel-root':{ fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused':{ color:'var(--brand)' },
};

const monthlyRevenue = [
  { month:'Jan', revenue:320000, commission:32000, orders:8 },
  { month:'Feb', revenue:380000, commission:38000, orders:10 },
  { month:'Mar', revenue:450000, commission:45000, orders:12 },
  { month:'Apr', revenue:420000, commission:42000, orders:11 },
  { month:'May', revenue:510000, commission:51000, orders:14 },
  { month:'Jun', revenue:590000, commission:59000, orders:16 },
  { month:'Jul', revenue:680000, commission:68000, orders:19 },
];
const maxRev = Math.max(...monthlyRevenue.map(m => m.revenue));

const providerData = [
  { name:'Mumbai Ads Pvt Ltd', listings:12, bookings:28, gross:840000, commission:84000 },
  { name:'Delhi Metro Ad Solutions', listings:8, bookings:19, gross:560000, commission:56000 },
  { name:'BLR Airport Ads', listings:5, bookings:14, gross:720000, commission:72000 },
  { name:'Pune Digital Displays', listings:7, bookings:11, gross:385000, commission:38500 },
  { name:'Hyderabad Outdoor Media', listings:2, bookings:4, gross:140000, commission:14000 },
];

// ── Reports ────────────────────────────────────────────────────────────────────
export const AdminReports: React.FC = () => {
  const [tab, setTab] = useState('revenue');

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Reports & Analytics" subtitle="Platform performance and financial insights"
        action={
          <button className="btn-ghost" style={{ padding:'9px 18px', fontSize:13, display:'flex', alignItems:'center', gap:7 }}>
            <Download size={14}/> Export Report
          </button>
        }/>

      {/* Tabs */}
      <div style={{ marginBottom:24 }}>
        <PillTabs
          tabs={[{ key:'revenue', label:'Revenue' }, { key:'commission', label:'Commission' }, { key:'occupancy', label:'Occupancy' }, { key:'providers', label:'Provider Earnings' }]}
          active={tab} onChange={setTab}/>
      </div>

      {/* KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb:3.5 }}>
        {[
          { title:'Total Revenue (YTD)', value:'₹53.2L', icon:<TrendingUp size={20}/>, change:'18% vs last year', positive:true, color:'var(--brand)' },
          { title:'Commission Earned', value:'₹4.82L', icon:<DollarSign size={20}/>, change:'12% vs last year', positive:true, color:'#22c55e' },
          { title:'Avg. Order Value', value:'₹1.13L', icon:<BarChart3 size={20}/>, change:'5% vs last month', positive:true, color:'#3b82f6' },
          { title:'Space Occupancy Rate', value:'78%', icon:<Percent size={20}/>, change:'3% vs last month', positive:true, color:'#8b5cf6' },
        ].map((s,i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}><StatCard {...s}/></Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        {/* Bar Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <SectionTitle>Monthly Revenue & Commission</SectionTitle>
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:200, paddingBottom:4 }}>
              {monthlyRevenue.map((m,i) => (
                <div key={m.month} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
                  <div style={{ fontSize:10, color:'var(--brand)', fontWeight:600 }}>₹{(m.revenue/100000).toFixed(1)}L</div>
                  <div style={{ width:'100%', display:'flex', gap:2, alignItems:'flex-end' }}>
                    <div style={{ flex:1, background:'linear-gradient(to top, #e8621a, #fb923c)', borderRadius:'4px 4px 0 0', height:`${(m.revenue/maxRev)*150}px`, transition:`height 0.7s ease ${i*0.08}s`, boxShadow:'0 2px 8px rgba(232,98,26,0.2)' }}/>
                    <div style={{ flex:1, background:'linear-gradient(to top, #22c55e, #4ade80)', borderRadius:'4px 4px 0 0', height:`${(m.commission/maxRev)*150}px`, transition:`height 0.7s ease ${i*0.08}s`, boxShadow:'0 2px 6px rgba(34,197,94,0.2)' }}/>
                  </div>
                  <span style={{ color:'var(--text-muted)', fontSize:10, fontFamily:'DM Sans' }}>{m.month}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:18, marginTop:12, justifyContent:'center' }}>
              {[{ color:'#e8621a', label:'Revenue' }, { color:'#22c55e', label:'Commission' }].map(l => (
                <div key={l.label} style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:l.color }}/>
                  <span style={{ color:'var(--text-muted)', fontSize:12, fontFamily:'DM Sans' }}>{l.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </Grid>

        {/* Orders by Month */}
        <Grid item xs={12} lg={4}>
          <Card>
            <SectionTitle>Orders by Month</SectionTitle>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {monthlyRevenue.map((m,i) => (
                <div key={m.month}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)' }}>{m.month} 2024</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--brand)' }}>{m.orders} orders</span>
                  </div>
                  <div style={{ height:5, background:'var(--bg-muted)', borderRadius:3 }}>
                    <div style={{ height:'100%', width:`${(m.orders/19)*100}%`, background:'linear-gradient(to right, #e8621a, #fb923c)', borderRadius:3, transition:`width 0.7s ease ${i*0.08}s` }}/>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Grid>

        {/* Provider Earnings Table */}
        <Grid item xs={12}>
          <Card style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'20px 24px 16px' }}>
              <SectionTitle>Provider Earnings Report</SectionTitle>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                <thead>
                  <tr style={{ background:'var(--bg-muted)' }}>
                    {['Provider','Listings','Bookings','Gross Earnings','Commission','Net Payout'].map(h=>(
                      <th key={h} style={{ color:'var(--text-muted)', fontFamily:'DM Sans', fontSize:11, fontWeight:600, textAlign:'left', padding:'11px 18px', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {providerData.map((r,i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border-light)' }}
                      onMouseEnter={e=>(e.currentTarget as HTMLTableRowElement).style.background='var(--bg-accent)'}
                      onMouseLeave={e=>(e.currentTarget as HTMLTableRowElement).style.background='#fff'}>
                      <td style={{ padding:'13px 18px', fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{r.name}</td>
                      <td style={{ padding:'13px 18px', color:'var(--text-secondary)', fontSize:13 }}>{r.listings}</td>
                      <td style={{ padding:'13px 18px', color:'var(--text-secondary)', fontSize:13 }}>{r.bookings}</td>
                      <td style={{ padding:'13px 18px', color:'var(--brand)', fontWeight:700, fontSize:13 }}>₹{r.gross.toLocaleString('en-IN')}</td>
                      <td style={{ padding:'13px 18px', color:'var(--warning)', fontSize:13, fontWeight:600 }}>₹{r.commission.toLocaleString('en-IN')}</td>
                      <td style={{ padding:'13px 18px', color:'var(--success)', fontWeight:700, fontSize:13 }}>₹{(r.gross-r.commission).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

// ── Settings ───────────────────────────────────────────────────────────────────
export const AdminSettings: React.FC = () => {
  const [gstRate, setGstRate] = useState('18');
  const [commission, setCommission] = useState('10');
  const [printRate, setPrintRate] = useState('6000');
  const [installRate, setInstallRate] = useState('4500');
  const [removalRate, setRemovalRate] = useState('2500');
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [whatsapp, setWhatsapp] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => { setSaved(true); setTimeout(()=>setSaved(false), 2500); };

  const switchSx = { '& .MuiSwitch-switchBase.Mui-checked':{ color:'var(--brand)' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':{ bgcolor:'var(--brand)' } };

  const SectionBox: React.FC<{ title:string; children:React.ReactNode }> = ({ title, children }) => (
    <Card>
      <h3 style={{ fontWeight:700, fontSize:15, color:'var(--text-primary)', margin:'0 0 18px', letterSpacing:'-0.01em' }}>{title}</h3>
      {children}
    </Card>
  );

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Platform Settings" subtitle="Configure taxes, commissions, charges and permissions"/>
      <Grid container spacing={2.5}>

        {/* Tax & Commission */}
        <Grid item xs={12} md={6}>
          <SectionBox title="Tax & Commission">
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <TextField label="GST Rate (%)" value={gstRate} onChange={e=>setGstRate(e.target.value)} fullWidth size="small" sx={inputSx} type="number"/>
              <TextField label="Default Platform Commission (%)" value={commission} onChange={e=>setCommission(e.target.value)} fullWidth size="small" sx={inputSx} type="number"/>
              <div style={{ background:'var(--warning-bg)', border:'1px solid #fde8a8', borderRadius:10, padding:'11px 14px' }}>
                <p style={{ color:'var(--warning)', fontFamily:'DM Sans', fontSize:12, margin:0, fontWeight:500 }}>⚠️ Commission is hidden from providers. Visible only to the Admin team.</p>
              </div>
            </div>
          </SectionBox>
        </Grid>

        {/* Service Charges */}
        <Grid item xs={12} md={6}>
          <SectionBox title="Service Charge Templates">
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <TextField label="Default Printing Charge (₹)" value={printRate} onChange={e=>setPrintRate(e.target.value)} fullWidth size="small" sx={inputSx} type="number"/>
              <TextField label="Default Installation Charge (₹)" value={installRate} onChange={e=>setInstallRate(e.target.value)} fullWidth size="small" sx={inputSx} type="number"/>
              <TextField label="Removal / Dismantling Charge (₹)" value={removalRate} onChange={e=>setRemovalRate(e.target.value)} fullWidth size="small" sx={inputSx} type="number"/>
            </div>
          </SectionBox>
        </Grid>

        {/* Notification Channels */}
        <Grid item xs={12} md={6}>
          <SectionBox title="Notification Channels">
            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {[
                { label:'Email Notifications', sublabel:'Order updates, approvals, payments', val:emailNotif, set:setEmailNotif },
                { label:'SMS Notifications', sublabel:'Booking confirmations and reminders', val:smsNotif, set:setSmsNotif },
                { label:'WhatsApp Notifications', sublabel:'Rich messages with campaign details', val:whatsapp, set:setWhatsapp },
              ].map(item => (
                <div key={item.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontWeight:500, fontSize:13, color:'var(--text-primary)' }}>{item.label}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{item.sublabel}</div>
                  </div>
                  <Switch checked={item.val} onChange={e=>item.set(e.target.checked)} sx={switchSx} size="small"/>
                </div>
              ))}
            </div>
          </SectionBox>
        </Grid>

        {/* RBAC */}
        <Grid item xs={12} md={6}>
          <SectionBox title="Role-Based Access Control">
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                { role:'Super Admin', perms:['All Access'], color:'var(--brand)' },
                { role:'Operations Manager', perms:['Listings','Bookings','Reports'], color:'#3b82f6' },
                { role:'Field Coordinator', perms:['Bookings','Staff Assignment'], color:'#8b5cf6' },
                { role:'Finance Manager', perms:['Payments','Reports','Invoices'], color:'#22c55e' },
              ].map(r => (
                <div key={r.role} style={{ background:'var(--bg-muted)', borderRadius:11, padding:'11px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, border:'1px solid var(--border-light)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:r.color }}/>
                    <span style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{r.role}</span>
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    {r.perms.map(p=>(
                      <span key={p} style={{ background:'#fff', border:'1px solid var(--border)', color:'var(--text-secondary)', padding:'2px 8px', borderRadius:5, fontSize:11, fontFamily:'DM Sans' }}>{p}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionBox>
        </Grid>

        {/* Security */}
        <Grid item xs={12} md={6}>
          <SectionBox title="Security">
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                { label:'Audit Logs', sublabel:'Track all admin actions', active:true },
                { label:'Secure File Uploads', sublabel:'Scan uploads for malware', active:true },
                { label:'Data Encryption', sublabel:'Encrypt sensitive data at rest', active:true },
                { label:'Two-Factor Auth', sublabel:'Require 2FA for admin logins', active:false },
              ].map(item => (
                <div key={item.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'var(--bg-muted)', borderRadius:10, border:'1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontWeight:500, fontSize:13, color:'var(--text-primary)' }}>{item.label}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{item.sublabel}</div>
                  </div>
                  <span style={{ background:item.active?'var(--success-bg)':'var(--bg-muted)', color:item.active?'var(--success)':'var(--text-muted)', border:`1px solid ${item.active?'#b8e8ce':'var(--border)'}`, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>
                    {item.active?'Enabled':'Disabled'}
                  </span>
                </div>
              ))}
            </div>
          </SectionBox>
        </Grid>

        {/* Save */}
        <Grid item xs={12}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button onClick={handleSave} className="btn-brand" style={{ padding:'12px 28px', fontSize:14 }}>
              {saved?'✓ Settings Saved!':'Save All Settings'}
            </button>
            {saved && <span style={{ color:'var(--success)', fontSize:13, fontWeight:500, fontFamily:'DM Sans' }}>Changes saved successfully.</span>}
          </div>
        </Grid>
      </Grid>
    </div>
  );
};
